import { useMemo, useState } from 'react';
import {
  ACTIVITY_IDS,
  FACTOR_KEYS,
  TOL_MULT,
  type FactorKey,
  type PresetActivityId,
} from '../../core/activities';
import { type PlannedSession } from '../../core/alerts';
import { ActivityIcon } from '../../ui/ActivityIcon';
import { AddToCalendar } from '../../ui/AddToCalendar';
import { getBlock } from '../../core/forecast';
import { buildShareUrl } from '../../core/shareLink';
import type { HourSlice } from '../../core/scoring';
import { formatHour } from '../../core/units';
import { useActivityName, useLocale, useT } from '../../hooks';
import { fill } from '../../i18n';
import { sessionToIcsEvent } from '../../lib/download';
import { maybeAskForReview } from '../../services/review';
import { shareLink } from '../../services/share';
import { fmtFull, fmtIsoTime } from '../../lib/format';
import { useForecast } from '../../state/forecast';
import { usePlanner } from '../../state/planner';
import { critFor, useSettings } from '../../state/settings';
import { useUi } from '../../state/ui';
import { Button, uiCss } from '../../ui/primitives';
import { RiskScale } from '../../ui/RiskScale';
import { Sheet } from '../../ui/Sheet';
import { FactorChips } from './FactorChips';
import { HourlyCharts } from './HourlyCharts';
import s from './detail.module.css';

const bandColor = (score: number) =>
  score < 25 ? 'var(--green)' : score < 55 ? 'var(--amber)' : 'var(--red)';
const bandBg = (score: number) =>
  score < 25 ? 'var(--green-bg)' : score < 55 ? 'var(--amber-bg)' : 'var(--red-bg)';

export function DetailSheet() {
  const t = useT();
  const locale = useLocale();
  const nameOf = useActivityName();
  const st = useSettings();
  const data = useForecast((f) => f.data);
  const { selected, detailOpen, closeDetail, tab, setTab, setTuneOpen } = useUi();
  const planner = usePlanner();

  const crit = useMemo(() => critFor(st, st.activity), [st]);
  const tolMult = TOL_MULT[st.tolerance];

  const [note, setNote] = useState('');
  const [copied, setCopied] = useState(false);
  // the note belongs to one block: reset it when the selection moves
  const [prevSel, setPrevSel] = useState(selected);
  if (selected !== prevSel) {
    setPrevSel(selected);
    setNote('');
    setCopied(false);
  }

  const block = useMemo(() => {
    if (!selected || !data) return null;
    const end = Math.min(selected.h + st.blockLen, st.hTo);
    const len = Math.max(1, end - selected.h);
    return { end, len, b: getBlock(data, selected.day, selected.h, len, crit, tolMult) };
  }, [selected, data, st.blockLen, st.hTo, crit, tolMult]);

  if (!selected || !data || !block?.b) {
    return <Sheet open={false} onClose={closeDetail} ariaLabel={t.detail.title} children={null} />;
  }

  const { b, end, len } = block;
  const { day, h } = selected;

  const already = planner.sessions.some(
    (p) => p.day === day && p.h === h && p.activityId === st.activity,
  );

  const dayHours: { h: number; slice: HourSlice }[] = [];
  const daySlices = data.days[day] ?? [];
  for (let hh = h; hh < h + len && hh < 24; hh++) {
    const slice = daySlices[hh];
    if (slice) dayHours.push({ h: hh, slice });
  }

  const di = data.daily.time.indexOf(day);
  const sunrise = data.daily.sunrise?.[di];
  const sunset = data.daily.sunset?.[di];

  const session = (id: number): PlannedSession => ({
    id,
    activityId: st.activity,
    day,
    h,
    len,
    locName: st.loc.name,
    lat: st.loc.lat,
    lon: st.loc.lon,
    baseScore: b.score,
    baseBand: b.band,
    ...(note.trim() ? { note: note.trim() } : {}),
  });

  const addToPlanner = () => {
    planner.add(session(Date.now()));
    void maybeAskForReview(usePlanner.getState().sessions.length);
  };

  // a link opens the same sport, place and block for whoever receives it;
  // custom activities exist on this device only, so they share the place alone
  const share = async () => {
    const preset = (ACTIVITY_IDS as readonly string[]).includes(st.activity)
      ? (st.activity as PresetActivityId)
      : undefined;
    const url = buildShareUrl({ activity: preset, place: st.loc, day, h });
    const text = fill(t.detail.shareText, {
      activity: nameOf(st.activity),
      when: `${fmtFull(day, locale)}, ${formatHour(h, st.clock)}–${formatHour(end, st.clock)}`,
      place: st.loc.name,
      verdict: t.risk[`${b.band}Short`],
    });
    const outcome = await shareLink(`${nameOf(st.activity)} · BlockCast`, text, url);
    if (outcome === 'copied') setCopied(true);
  };

  // which weighted factor drives the score — same maths as riskScore's "worst"
  let domKey: FactorKey | null = null;
  let domEff = 0;
  for (const k of FACTOR_KEYS) {
    const eff = Math.min(1, b.f.sev[k] * tolMult) * (crit.weights[k] / 10);
    if (eff > domEff) {
      domEff = eff;
      domKey = k;
    }
  }
  const dominant = domEff >= 0.2 ? domKey : null;

  // the tune panel lives on the Today and Week views, under the same header
  const openTune = () => {
    closeDetail();
    if (tab !== 'today' && tab !== 'week') setTab('week');
    setTuneOpen(true);
  };

  return (
    <Sheet open={detailOpen} onClose={closeDetail} ariaLabel={t.detail.title}>
      <div className={s.top}>
        <div className={s.gauge} style={{ background: bandBg(b.score), color: bandColor(b.score) }}>
          {b.score}
          <RiskScale score={b.score} />
        </div>
        <div>
          <div className={s.when}>
            <ActivityIcon id={st.activity} /> {nameOf(st.activity)} · {fmtFull(day, locale)}
          </div>
          <div className={s.verdict}>
            {formatHour(h, st.clock)} – {formatHour(end, st.clock)} · {t.risk[b.band]}
            {dominant ? ` · ${fill(t.detail.mostly, { factor: t.tune[dominant] })}` : ''}
          </div>
        </div>
      </div>

      <FactorChips b={b} crit={crit} tolMult={tolMult} units={st.units} t={t} />

      <button className={s.whyTune} onClick={openTune}>
        <span>⚙ {fill(t.detail.scoredFor, { activity: nameOf(st.activity) })}</span>
        <b>{t.detail.adjust} →</b>
      </button>

      <HourlyCharts
        hours={dayHours}
        units={st.units}
        clock={st.clock}
        t={t}
        sun={
          sunrise && sunset
            ? {
                rise: fmtIsoTime(sunrise, locale, st.clock),
                set: fmtIsoTime(sunset, locale, st.clock),
              }
            : null
        }
      />

      <div className={s.planExtras}>
        <input
          className={`${uiCss.input} ${s.noteInput}`}
          value={note}
          placeholder={t.planner.notePh}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      <div className={s.actions}>
        <Button onClick={addToPlanner} disabled={already}>
          {already ? `✓ ${t.detail.inPlanner}` : `➕ ${t.detail.addPlanner}`}
        </Button>
        <AddToCalendar dropUp items={[{ event: sessionToIcsEvent(session(0), b, t, nameOf) }]} />
        <Button variant="ghost" onClick={() => void share()}>
          {copied ? `✓ ${t.detail.shareCopied}` : `↗ ${t.detail.share}`}
        </Button>
      </div>
    </Sheet>
  );
}
